/* FLEIT Studio. No third-party JavaScript. Progressive enhancement only. */
(() => {
  'use strict';

  function closeDisclosures(except) {
    document.querySelectorAll('details[data-close-outside][open]').forEach((details) => {
      if (details !== except) details.open = false;
    });
  }
  document.addEventListener('click', (event) => {
    const disclosure = event.target.closest('details[data-close-outside]');
    closeDisclosures(disclosure);
    if (disclosure && event.target.closest('a')) disclosure.open = false;
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('details[data-close-outside][open]');
    if (open) { open.open = false; open.querySelector('summary')?.focus(); }
  });

  // Variant data is fetched from Shopify. Never download a full product JSON
  // or assume the catalog has fewer than 250 variants.
  class FleitProduct extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.abortController = null;
      this.requestSequence = 0;
      this.addEventListener('change', (event) => {
        if (event.target.matches('.fl-option-select')) this.changeVariant(event.target);
      });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-thumbnail]');
        if (!button) return;
        this.querySelectorAll('[data-media]').forEach((figure) => {
          figure.hidden = figure.dataset.media !== button.dataset.thumbnail;
        });
        this.querySelectorAll('[data-thumbnail]').forEach((thumb) => {
          thumb.setAttribute('aria-pressed', String(thumb === button));
        });
      });
    }

    disconnectedCallback() { this.abortController?.abort(); }

    async changeVariant(changedSelect) {
      this.abortController?.abort();
      const controller = new AbortController();
      this.abortController = controller;
      const sequence = ++this.requestSequence;
      const focusPosition = changedSelect.dataset.optionPosition;
      const oldScrollY = window.scrollY;
      const formState = new Map();
      this.querySelectorAll('input[name="quantity"], [name^="properties["]').forEach((input) => {
        formState.set(input.name, input.value);
      });
      const selected = changedSelect.selectedOptions[0];
      const productUrl = selected?.dataset.productUrl || this.dataset.productUrl;
      const url = new URL(productUrl, window.location.origin);
      // All product URLs must remain on the current store's origin.
      if (url.origin !== window.location.origin) return;
      const optionIds = Array.from(this.querySelectorAll('.fl-option-select')).map((select) => select.value);
      url.searchParams.set('option_values', optionIds.join(','));
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.setAttribute('aria-busy', 'true');
      const submitButton = this.querySelector('button[name="add"]');
      if (submitButton) submitButton.disabled = true;
      try {
        const response = await fetch(url, {
          signal: controller.signal,
          credentials: 'same-origin',
          headers: { 'Accept': 'text/html' }
        });
        if (!response.ok) throw new Error(`Shopify ${response.status}`);
        const html = await response.text();
        if (sequence !== this.requestSequence) return;
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        const replacement = documentFragment.querySelector('fleit-product');
        if (!replacement) throw new Error('Product section missing');
        // Shopify's response is same-origin server-rendered theme content.
        this.innerHTML = replacement.innerHTML;
        this.dataset.productUrl = replacement.dataset.productUrl;
        this.querySelectorAll('input[name="quantity"], [name^="properties["]').forEach((input) => {
          if (!formState.has(input.name)) return;
          if (input.name === 'quantity') {
            const step = Math.max(1, Number(input.step) || 1);
            const min = Math.max(1, Number(input.min) || 1);
            const max = input.max ? Number(input.max) : Infinity;
            const desired = Number(formState.get(input.name)) || min;
            input.value = String(Math.min(max, min + Math.max(0, Math.floor((desired - min) / step)) * step));
          } else input.value = formState.get(input.name);
        });
        const variantId = this.querySelector('input[name="id"]')?.value;
        const currentUrl = new URL(this.dataset.productUrl, window.location.origin);
        if (variantId) currentUrl.searchParams.set('variant', variantId);
        else currentUrl.searchParams.set('option_values', optionIds.join(','));
        history.replaceState({}, '', currentUrl.pathname + currentUrl.search);
        const focused = Array.from(this.querySelectorAll('.fl-option-select')).find((select) => select.dataset.optionPosition === focusPosition);
        focused?.focus({ preventScroll: true });
        window.scrollTo({ top: oldScrollY, behavior: 'instant' });
        const price = this.querySelector('.fl-product__price')?.textContent.trim();
        const live = document.querySelector('.fl-live-region');
        if (live && price) live.textContent = price;
      } catch (error) {
        if (error.name === 'AbortError') return;
        const errorElement = this.querySelector('.fl-variant-error');
        if (errorElement) { errorElement.textContent = this.dataset.error; errorElement.hidden = false; }
        // Do not enable the old variant after a failed selection update.
        const variantInput = this.querySelector('input[name="id"]');
        if (variantInput) variantInput.disabled = true;
      } finally {
        if (sequence === this.requestSequence) this.removeAttribute('aria-busy');
      }
    }
  }
  if (!customElements.get('fleit-product')) customElements.define('fleit-product', FleitProduct);

  // Country/province pickers for Shopify's optional legacy customer accounts.
  function initAddresses(root = document) {
    root.querySelectorAll('[data-address-country]').forEach((country) => {
      if (country.dataset.initialized) return;
      country.dataset.initialized = 'true';
      const province = document.getElementById(country.dataset.provinceId);
      const container = province?.closest('.fl-field');
      const update = () => {
        if (!province) return;
        let provinces = [];
        try { provinces = JSON.parse(country.selectedOptions[0]?.dataset.provinces || '[]'); } catch (_) { /* no regions */ }
        province.replaceChildren();
        provinces.forEach(([value, label]) => province.add(new Option(label, value)));
        if (province.dataset.default) province.value = province.dataset.default;
        if (container) container.hidden = !provinces.length;
        province.disabled = !provinces.length;
      };
      if (country.dataset.default) country.value = country.dataset.default;
      update();
      country.addEventListener('change', update);
    });
  }
  document.addEventListener('submit', (event) => {
    const form = event.target;
    if (form.matches('[data-confirm-delete]') && !window.confirm(form.dataset.confirmDelete)) event.preventDefault();
  });
  function openRecovery() { if (location.hash === '#Recover') document.getElementById('Recover')?.setAttribute('open', ''); }
  window.addEventListener('hashchange', openRecovery);
  openRecovery();
  initAddresses();
  document.addEventListener('shopify:section:load', (event) => initAddresses(event.target));
})();
